import { FC, useMemo } from 'react';
import { Card } from 'shared/ui/card/card';
import { Icon } from 'shared/ui/icon/icon';
import { ScoreChart } from 'shared/ui/score-chart';
import cls from './score-card.module.scss';
import { useCachedSelectedProject, useTranslation } from 'hooks';
import { IconTooltip } from 'shared/ui/icon-tooltip/icon-tooltip';
import { CompetitorRow } from './competitor-row';
import { ScoreCardProps } from './score-card.types';
import { Skeleton } from 'shared/ui/skeleton/skeleton';
import { Link } from 'react-router-dom';
import { track } from 'utils/mixpanel';
import { VisibilityEntityBase } from 'store/api/apiTypes';
import { appRoute } from 'shared/constants/routes';

export const ScoreCard: FC<ScoreCardProps> = (props: ScoreCardProps) => {
  const {
    score,
    displayScore,
    scoreChange,
    competitors,
    selectCompetitor,
    selectedCompetitor,
    className,
    description,
    trackName,
    productNumber,
    title = 'visibility',
    size = 'large',
    isLoading = false,
  } = props;

  const handleSelectCompetitor = (competitor?: VisibilityEntityBase) => {
    selectCompetitor?.(competitor);
  };

  const isLarge = size === 'large';
  const { t } = useTranslation();

  const { selectedProject, isTrial, testingB } = useCachedSelectedProject();

  const projectAsCompetitor = useMemo<VisibilityEntityBase>(
    () => ({
      domain: selectedProject?.domain ?? '',
      overall_score: score,
      description: '',
      name: '',
      mention_rate: score,
      total_mentions: 0,
      mean_rank: 0,
      query_metrics: [],
    }),
    [selectedProject?.domain, score]
  );

  const cardCompetitors = useMemo(() => {
    const others = (competitors ?? []).filter(
      c => c.domain !== selectedProject?.domain
    );
    return [projectAsCompetitor, ...others];
  }, [competitors, projectAsCompetitor, selectedProject?.domain]);

  const maxScore = useMemo(
    () => Math.max(...cardCompetitors.map(c => c.mention_rate).concat(0)),
    [cardCompetitors]
  );

  cardCompetitors?.sort((a, b) => b.mention_rate - a.mention_rate);
  const currentFirst = cardCompetitors[0].domain === selectedProject?.domain;
  const displayedScore = selectedCompetitor?.mention_rate ?? score;
  const displayedScoreLabel =
    selectedCompetitor?.mention_rate ?? displayScore ?? score;

  return (
    <Card className={`${cls.scoreCard} ${className || ''}`}>
      <div className={cls.content}>
        <div className={cls.titleWrapper}>
          <h3 className={cls.title}>{t(title)}</h3>
          <IconTooltip
            id={`tooltip-${title}`}
            onMouseEnter={
              trackName
                ? () =>
                    track(
                      `AIS:${productNumber ? 'PROD' : 'DSH'}_${trackName}-TOOLTIP-HOVER`,
                      {
                        is_trial: isTrial,
                        ...(productNumber && { product: productNumber }),
                      }
                    )
                : undefined
            }
          >
            <span>{t(`${title}Tooltip`)}</span>
          </IconTooltip>
        </div>
        <div className={cls.score}>
          {isLoading ? (
            <Skeleton width={80} height={50} />
          ) : (
            <p>
              <span className={cls.value}>{displayedScoreLabel}</span>
              /10
            </p>
          )}
          {scoreChange && (
            <div>
              <Icon pathToIcon={''} />
              <p>
                <span
                  className={`${cls.change} ${parseFloat(scoreChange) > 0 ? cls.green : cls.red}`}
                >
                  {scoreChange}
                </span>
                {t('lastReportResult', undefined, 'last report result')}
              </p>
            </div>
          )}
        </div>
        <div className={cls.chart}>
          {isLoading ? (
            <Skeleton height={90} />
          ) : (
            <ScoreChart
              score={displayedScore}
              change={scoreChange ? parseFloat(scoreChange) : undefined}
            />
          )}
        </div>
        {!isLarge && description && (
          <p className={cls.description}>{description}</p>
        )}
        {isLarge && (
          <>
            <div className={cls.competitorsHeader}>
              <h4 className={cls.competitorsTitle}>
                {t('competitorsComparison')}
              </h4>
              <IconTooltip
                id='competitors-tt'
                onMouseEnter={() =>
                  track('AIS:PROD_COMPETITORS-TOOLTIP-HOVER', {
                    product: productNumber,
                    is_trial: isTrial,
                  })
                }
              >
                <span>{t('competitorsTooltip')}</span>
              </IconTooltip>
            </div>
            <div className={cls.competitors}>
              {cardCompetitors?.map((competitor, index) => (
                <CompetitorRow
                  isSelected={selectedCompetitor?.domain === competitor.domain}
                  key={competitor.domain}
                  competitor={competitor}
                  maxScore={maxScore}
                  currentDomain={selectedProject?.domain}
                  total={cardCompetitors.length}
                  onSelect={handleSelectCompetitor}
                  productNumber={productNumber}
                  competitorNumber={index + 1}
                  isTrial={isTrial}
                  restricted={
                    isTrial &&
                    testingB &&
                    (currentFirst ? index !== 1 : index !== 0)
                  }
                />
              ))}
            </div>
          </>
        )}
        {isLarge &&
          (isTrial && testingB ? (
            <Card className={cls.explore}>
              <h4>{t('trialCompetitors')}</h4>
              <p>{t('trialCompetitorsDescription')}</p>
              <Link to={appRoute('/payment')}>{t('upgrade')}</Link>
            </Card>
          ) : (
            <Card className={cls.explore}>
              <h4>{t('exploreCompetitors')}</h4>
              <p>{t('exploreCompetitorsDescription')}</p>
            </Card>
          ))}
      </div>
    </Card>
  );
};
